/**
 * Busca e resolução de posições (endereços) — pesquisa case-sensitive na API.
 * Sempre exige `warehouse_id` ativo no filtro (API valida).
 */
import api, { getSialApiHost } from '../api.js';
import db from '../../versa/db.js';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function warehousesActiveOnly(rows)
{
	return (Array.isArray(rows) ? rows : []).filter(
		(r) => !(r.is_disabled === true || r.is_disabled === 1 || r.is_disabled === '1')
	);
}

function escapeFilterToken(s)
{
	return String(s ?? '').replace(/[*,]/g, '');
}

/** Endereço completo digitado (ex.: 5-O-10-1) — busca exata cb+ce em vez de contém. */
function looksLikeFullLocationAddress(s)
{
	const t = String(s ?? '').trim();
	return /^[\dA-Za-z]+(-[\dA-Za-z]+){2,}$/.test(t);
}

/** Rótulo do endereço (ex.: A-1-1). */
export function fmtLocAddr(a)
{
	return Array.isArray(a) ? a.join('-') : String(a ?? '');
}

/**
 * Resolve empresa (UUID) a partir de id ou nick digitado no formulário.
 * @param {string} raw
 * @returns {Promise<string|null>}
 */
export async function resolveCompanyId(raw)
{
	const v = String(raw ?? '').trim();
	if (!v)
		return null;
	if (UUID_REGEX.test(v))
		return v;
	const companies = await db.list('companies').catch(() => []);
	const c = companies.find(x =>
		(x.nick || '') === v || String(x.nick || '') === v
		|| (x.name || '') === v || String(x.name || '') === v);
	const id = c?.id;
	return id && UUID_REGEX.test(String(id)) ? String(id) : null;
}

/**
 * Resolve armazém ativo por id numérico ou nome (ex.: SES02).
 * @param {string} companyId
 * @param {string|number} raw
 * @returns {Promise<number>}
 */
export async function resolveWarehouseId(companyId, raw)
{
	const v = String(raw ?? '').trim();
	if (!v)
		return NaN;
	const n = Number(v);
	if (Number.isInteger(n) && n >= 1)
		return n;
	const warehouses = warehousesActiveOnly(await db.list('warehouses').catch(() => []));
	const scoped = companyId
		? warehouses.filter((w) => String(w.company_id) === String(companyId))
		: warehouses;
	const w = scoped.find((x) =>
		(x.name || '') === v
		|| String(x.name || '') === v
		|| String(x.id) === v);
	return w?.id != null ? Number(w.id) : NaN;
}

/**
 * Lista posições do armazém na API; com `search`, filtro no endereço (cc ou cb+ce exato).
 * @param {string} companyId
 * @param {number} warehouseId
 * @param {{ search?: string, limit?: number, silent?: boolean, exactAddress?: boolean, isExpedition?: boolean }} [opts]
 * @returns {Promise<object[]>}
 */
export async function fetchWarehouseLocations(companyId, warehouseId, opts = {})
{
	const co = String(companyId ?? '').trim();
	const wh = Number(warehouseId);
	if (!co || !Number.isInteger(wh) || wh < 1)
		return [];
	const parts = [`company_id*eq*${co}`, `warehouse_id*eq*${wh}`];
	if (opts.isExpedition === true)
		parts.push('is_expedition*eq*true');
	const term = String(opts.search ?? '').trim();
	if (term)
	{
		const tok = escapeFilterToken(term);
		if (opts.exactAddress || looksLikeFullLocationAddress(term))
			parts.push(`address*cb*${tok},address*ce*${tok}`);
		else
			parts.push(`address*cc*${tok}`);
	}
	const params = new URLSearchParams();
	params.append('filter', parts.join(','));
	params.append('sort', '4');
	params.append('limit', String(term ? (opts.limit ?? 80) : (opts.limit ?? 0)));
	const q = `?${params.toString()}`;
	const rows = opts.silent
		? await fetchLocationsSilent(q)
		: await api.call(api.list_locations, q);
	return Array.isArray(rows) ? rows : [];
}

/** Busca posições sem msgbox «Carregando…» (digitação no datalist). */
async function fetchLocationsSilent(query)
{
	try
	{
		const res = await fetch(`${getSialApiHost()}/locations${query}`, {
			method: 'GET',
			credentials: 'include',
		});
		if (!res.ok)
			return [];
		const data = await res.json();
		return Array.isArray(data) ? data : [];
	}
	catch (_)
	{
		return [];
	}
}

/**
 * Resolve id da posição a partir de id numérico ou texto do endereço (case-sensitive).
 * @param {string} companyId
 * @param {number|string} warehouseId
 * @param {string|number} raw
 * @param {{ isExpedition?: boolean }} [opts]
 * @returns {Promise<number>}
 */
export async function resolveLocationIdForMove(companyId, warehouseId, raw, opts = {})
{
	const rawStr = String(raw ?? '').trim();
	if (!rawStr)
		return NaN;
	const n = Number(rawStr);
	if (Number.isInteger(n) && n >= 1)
		return n;
	const co = await resolveCompanyId(companyId) || companyId;
	const wh = await resolveWarehouseId(co, warehouseId);
	if (!Number.isInteger(wh) || wh < 1)
		return NaN;
	const exact = looksLikeFullLocationAddress(rawStr);
	const rows = await fetchWarehouseLocations(co, wh, {
		search: rawStr,
		limit: 80,
		silent: true,
		exactAddress: exact,
		isExpedition: opts.isExpedition === true,
	});
	if (!rows.length)
		return NaN;
	if (rows.length === 1)
		return Number(rows[0].id);
	const want = rawStr;
	for (const r of rows)
	{
		if (fmtLocAddr(r.address) === want)
			return Number(r.id);
	}
	return NaN;
}
